import { useCallback, useState } from "react";
import { reorder, reorderQuoteMap } from "../Reorder";

const generateId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

const DEFAULT_COLUMN_KEYS = [
  "obj-one",
  "obj-two",
  "obj-three",
  "obj-four",
  "obj-five",
  "obj-six",
  "obj-seven",
  "obj-eight",
  "obj-nine",
  "obj-ten",
];

const buildDefaultObjectives = () =>
  DEFAULT_COLUMN_KEYS.reduce((acc, key) => {
    acc[key] = { title: "", score: 0, active: 0, subTasks: [] };
    return acc;
  }, {});

const calculateObjectiveScore = (subTasks) => {
  const denominator = subTasks.length;
  if (denominator === 0) return "0.00";

  const numerator = subTasks.reduce((total, task) => {
    if (task.completed === 1) return total + 1;
    if (task.steps && task.steps.length > 0) {
      const completedSteps = task.steps.filter((s) => s.completed === 1).length;
      return total + completedSteps / task.steps.length;
    }
    return total;
  }, 0);

  return (numerator / denominator).toFixed(2);
};

const updateColumn = (objectives, columnId, updater) => {
  const column = objectives[columnId];
  if (!column || !Array.isArray(column.subTasks)) return objectives;

  const nextSubTasks = updater(column.subTasks);
  if (nextSubTasks === column.subTasks) return objectives;

  return {
    ...objectives,
    [columnId]: {
      ...column,
      subTasks: nextSubTasks,
      score: calculateObjectiveScore(nextSubTasks),
    },
  };
};

const mapTaskById = (subTasks, taskId, transform) => {
  let changed = false;
  const next = subTasks.map((task) => {
    if (task.id !== taskId) return task;
    changed = true;
    return transform(task);
  });
  return changed ? next : subTasks;
};

export default function useObjectivesState() {
  const [objectives, setObjectives] = useState(buildDefaultObjectives);
  const [columnOrder, setColumnOrder] = useState([]);

  // ---------- Column-level ----------

  const changeObjectiveTitle = useCallback((event, order) => {
    const newTitle = event.target.value;
    const name = event.target.name;

    setObjectives((prev) => {
      const reordered = order.reduce((acc, key) => {
        if (prev[key]) acc[key] = prev[key];
        return acc;
      }, {});

      const target = reordered[name];
      if (!target) return prev;

      reordered[name] = {
        ...target,
        title: newTitle,
        score: target.score ?? 0,
      };
      return reordered;
    });
  }, []);

  const deleteObjective = useCallback((columnId) => {
    setObjectives((prev) => ({
      ...prev,
      [columnId]: { title: "", score: 0, active: 0, subTasks: [] },
    }));
  }, []);

  const reorderObjectives = useCallback((newOrder) => {
    setObjectives((prev) =>
      newOrder.reduce((acc, key) => {
        if (prev[key]) acc[key] = prev[key];
        return acc;
      }, {}),
    );
  }, []);

  const handleSort = useCallback((event, columnId) => {
    const { name: sortStyle, sort: sortType } = event.target.dataset;

    setObjectives((prev) => {
      const column = prev[columnId];
      if (!column?.subTasks) return prev;

      const sorted = [...column.subTasks].sort((a, b) => {
        if (sortStyle === "urge") {
          return sortType === "asc"
            ? (a.urgency ?? 0) - (b.urgency ?? 0)
            : (b.urgency ?? 0) - (a.urgency ?? 0);
        }
        return sortType === "asc"
          ? new Date(a.deadline) - new Date(b.deadline)
          : new Date(b.deadline) - new Date(a.deadline);
      });

      return { ...prev, [columnId]: { ...column, subTasks: sorted } };
    });
  }, []);

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const { source, destination } = result;

      if (result.type === "COLUMN") {
        const reorderedOrder = reorder(columnOrder, source.index, destination.index);
        setColumnOrder(reorderedOrder);
        setObjectives((prev) => {
          const reordered = {};
          reorderedOrder.forEach((key) => {
            reordered[key] = prev[key];
          });
          return reordered;
        });
        return;
      }

      setObjectives((prev) => {
        const subTaskMap = Object.keys(prev).reduce((acc, key) => {
          acc[key] = prev[key].subTasks;
          return acc;
        }, {});

        const data = reorderQuoteMap({ quoteMap: subTaskMap, source, destination });

        const next = { ...prev };
        Object.keys(data.quoteMap).forEach((key) => {
          const newSubTasks = data.quoteMap[key];
          next[key] = {
            ...next[key],
            subTasks: newSubTasks,
            score: calculateObjectiveScore(newSubTasks),
          };
        });
        return next;
      });
    },
    [columnOrder],
  );

  // ---------- Subtask-level (by task id) ----------

  const addSubTask = useCallback((event) => {
    const description = event.target.value;
    const columnId = event.target.name;
    if (!description) return;

    setObjectives((prev) => {
      const column = prev[columnId];
      if (!column) return prev;

      const newSubTasks = [
        ...column.subTasks,
        {
          id: generateId(columnId),
          description,
          steps: [],
          notes: "",
          completed: 0,
          estimate: null,
        },
      ];

      return {
        ...prev,
        [columnId]: {
          ...column,
          subTasks: newSubTasks,
          score: calculateObjectiveScore(newSubTasks),
        },
      };
    });

    event.target.value = "";
  }, []);

  const toggleCompletedSubTasks = useCallback((taskId, columnId) => {
    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => ({
          ...task,
          completed: task.completed === 0 ? 1 : 0,
        })),
      ),
    );
  }, []);

  const handleEstimateChange = useCallback((taskId, columnId, value) => {
    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => {
          if (value === null) {
            const { estimate, ...rest } = task;
            return rest;
          }
          return { ...task, estimate: value };
        }),
      ),
    );
  }, []);

  const changeSubTaskUrgency = useCallback((event, taskId, columnId, ref) => {
    const dataset = event.currentTarget.dataset;
    const targetColumn = dataset.name || event.target.name || columnId;
    const targetValue = dataset.value || event.target.value;
    const displayName = dataset.displayName;

    setObjectives((prev) =>
      updateColumn(prev, targetColumn, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => {
          if (displayName === "remove") {
            const next = { ...task };
            delete next[ref];
            return next;
          }
          if (displayName === "urgency") {
            return { ...task, urgency: parseInt(targetValue, 10) };
          }
          return { ...task, deadline: targetValue };
        }),
      ),
    );
  }, []);

  const updateDescription = useCallback((eventOrValue, columnId, taskId) => {
    const newDescription =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue.target.value;

    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => ({
          ...task,
          description: newDescription,
        })),
      ),
    );
  }, []);

  const updateNotes = useCallback((columnId, taskId, event) => {
    const newNotes = event.target.value;
    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => ({ ...task, notes: newNotes })),
      ),
    );
  }, []);

  const deleteSubtask = useCallback((columnId, taskId) => {
    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) => {
        const filtered = subTasks.filter((task) => task.id !== taskId);
        return filtered.length === subTasks.length ? subTasks : filtered;
      }),
    );
  }, []);

  // ---------- Step-level (by task id + step id) ----------

  const addStep = useCallback((eventOrValue, columnId, taskId) => {
    const description =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value;
    if (!description) return;

    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => ({
          ...task,
          steps: [
            ...(task.steps || []),
            { id: generateId(task.id), action: description, completed: 0 },
          ],
        })),
      ),
    );

    if (eventOrValue?.target) eventOrValue.target.value = "";
  }, []);

  const toggleStep = useCallback((taskId, stepId, columnId) => {
    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => {
          const steps = task.steps || [];
          let changed = false;
          const newSteps = steps.map((step) => {
            if (step.id !== stepId) return step;
            changed = true;
            return { ...step, completed: step.completed === 0 ? 1 : 0 };
          });
          return changed ? { ...task, steps: newSteps } : task;
        }),
      ),
    );
  }, []);

  const updateStep = useCallback((columnId, taskId, stepId, event) => {
    const value = event.target.value;
    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => {
          const steps = task.steps || [];
          let changed = false;
          const newSteps = steps.map((step) => {
            if (step.id !== stepId) return step;
            changed = true;
            return { ...step, action: value };
          });
          return changed ? { ...task, steps: newSteps } : task;
        }),
      ),
    );
  }, []);

  const deleteStep = useCallback((columnId, taskId, stepId) => {
    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => {
          const steps = task.steps || [];
          const filtered = steps.filter((step) => step.id !== stepId);
          return filtered.length === steps.length
            ? task
            : { ...task, steps: filtered };
        }),
      ),
    );
  }, []);

  const onDragEndStep = useCallback((result, columnId, taskId) => {
    const { destination, source } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    setObjectives((prev) =>
      updateColumn(prev, columnId, (subTasks) =>
        mapTaskById(subTasks, taskId, (task) => {
          const steps = [...(task.steps || [])];
          const [removed] = steps.splice(source.index, 1);
          steps.splice(destination.index, 0, removed);
          return { ...task, steps };
        }),
      ),
    );
  }, []);

  return {
    objectives,
    columnOrder,
    setObjectives,
    setColumnOrder,

    // column
    changeObjectiveTitle,
    deleteObjective,
    reorderObjectives,
    handleSort,
    onDragEnd,

    // subtask
    addSubTask,
    toggleCompletedSubTasks,
    handleEstimateChange,
    changeSubTaskUrgency,
    updateDescription,
    updateNotes,
    deleteSubtask,

    // step
    addStep,
    toggleStep,
    updateStep,
    deleteStep,
    onDragEndStep,
  };
}
